/**
 *
 * Copyright (c) 2019-present, wobe-systems GmbH
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * to be found in the LICENSE file in the root directory of this source tree.
 */

/**
 * Returns a helper function to feed into Array.sort()
 * for sorting an array of objects by one or more properties
 *
 * @param properties
 *     a property name or a list of property names to sort by
 *     for descing sort prepend a "-" to the name of the property
 */

export const sortObjectsByProperties = (
  properties: string | string[],
): ((a: any, b: any) => number
) => {
  let fields = properties;

  if (typeof fields === 'string') {
    fields = [fields];
  }

  // extract asc/desc sorting for each field
  const directions: number[] = [];
  fields = fields.map((field, index) => {
    if (field[0] === '-') {
      directions[index] = -1;
      return field.substring(1);
    }

    directions[index] = 1;
    return field;
  });

  return (a: any, b: any): number => {
    let index;
    for (index = 0; index < fields.length; index += 1) {
      const property = fields[index];
      const valA = a[property];
      const valB = b[property];

      if (valA < valB || (valA !== undefined && valB === undefined)) {
        return -directions[index];
      }

      if (valA > valB || (valA === undefined && valB !== undefined)) {
        return directions[index];
      }
    }
    return 0;
  };
};

/**
 * Helper function to feed into the Array.sort()
 * for sorting an array of objects by name
 *
 * @param firstObject The first object to compare
 * @param secondObject The second object to compare
 */
export const sortByName = (firstObject: any, secondObject: any): number => {
  // Use toUpperCase() to ignore character casing
  const firstObjectName = firstObject.name.toUpperCase();
  const secondObjectName = secondObject.name.toUpperCase();

  return firstObjectName > secondObjectName ? 1 : -1;
};

/**
 * Helper function insert an object into a
 * sorted list by name
 *
 * @param sortedList A sorted list of objects
 * @param objectToInsert The object to be placed alphabetically
 */
export const insertByName = (sortedList: any, objectToInsert: any) => {
  // Traverse list to find place for object to insert
  let elementInserted = false;
  sortedList.every((objectToCompare: any, index: number) => {
    if (objectToInsert.name.toUpperCase() < objectToCompare.name.toUpperCase()) {
      sortedList.splice(index, 0, objectToInsert);

      elementInserted = true;
      return false;
    }

    return true;
  });

  if (!elementInserted) {
    sortedList.push(objectToInsert);
  }
};
